import React, { ReactElement, useMemo, useState } from 'react'
import { PrimaryButton } from './Button'
import Radom, { WithdrawalStatus } from '../state/Radom'
import { InputLabel } from './Input'
import { EXPORT_COLUMN_TYPES, EXPORT_TIME_RANGES, EXPORT_WITHDRAWALS_COLUMNS as COLUMNS, RADOM_COLORS } from '../util/Constants'
import Checkbox from './Checkbox'
import { endOfDay, intlFormat, startOfDay } from 'date-fns'
import { downloadBlob, errorToast, getExportFileName } from '../util/Util'
import DateSelector from './DateSelector'
import { cloneDeep } from 'lodash'
import Dropdown, { DropdownItem } from './Dropdown'
import { DateRange } from './Shared'

const ExportWithdrawalsForm = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState(EXPORT_TIME_RANGES[0])
  const [closeFn, setCloseFn] = useState(() => () => {})
  const [customFromDate, setCustomFromDate] = useState<Date | undefined>(undefined)
  const [customToDate, setCustomToDate] = useState<Date | undefined>(undefined)
  const [columnTypes, setColumnTypes] = useState(EXPORT_COLUMN_TYPES.ALL_COLUMNS)
  const [columns, setColumns] = useState(cloneDeep(COLUMNS))
  const [withdrawalStatuses, setWithdrawalStatuses] = useState<WithdrawalStatus[]>([
    WithdrawalStatus.Complete,
    WithdrawalStatus.Failed,
    WithdrawalStatus.Pending,
    WithdrawalStatus.Processing
  ])

  const columnsAsStrings = useMemo(() =>
    Object.entries(columns).filter(([_, v]) => !v.unchecked).map(([k]) => k), [columns])

  const exportWithdrawals = async (): Promise<void> => {
    let fromDate, toDate
    if (selectedTimeRange.label === 'Custom') {
      if (customFromDate && customToDate) {
        fromDate = startOfDay(customFromDate).toISOString()
        toDate = endOfDay(customToDate).toISOString()
      } else {
        errorToast('Must select from and to custom date ranges')
        return
      }
    } else if (selectedTimeRange.dateFn) {
      fromDate = selectedTimeRange.dateFn().from.toISOString()
      toDate = selectedTimeRange.dateFn().to.toISOString()
    }
    setIsLoading(true)

    Radom.exportWithdrawals(
      fromDate,
      toDate,
      columnsAsStrings,
      withdrawalStatuses
    )
      .then(data => {
        const blob = new Blob(
          [data],
          { type: 'text/csv' }
        )

        const fileName = getExportFileName('withdrawals')

        downloadBlob(blob, fileName)
      })
      .catch(err => {
        errorToast(err.response?.data?.error || err.message)
      }).finally(() => {
        setIsLoading(false)
      })
  }

  return <>
    <div style={{
      padding: '0 30px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      fontSize: 14,
      width: 600
    }}>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <InputLabel>Date range</InputLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {EXPORT_TIME_RANGES.map(t =>
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

                <DateRange onClick={() => setSelectedTimeRange(t)} disabled={isLoading}>
                  <Checkbox isRadio checked={selectedTimeRange === t} size={18} disabled={isLoading} />
                  <span>{t.label}</span>
                </DateRange>
                {
                  selectedTimeRange.label === 'Custom' && t.label === 'Custom' &&
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <DateSelector onChange={d => setCustomFromDate(d)} value={customFromDate} />
                  <span>-</span>
                  <DateSelector onChange={d => setCustomToDate(d)} value={customToDate} />
                </div>
                }
              </div>
              {
                t.dateFn && t.label !== 'All' && t.label !== 'Today' &&
                 <div style={{ display: 'flex', opacity: 0.5 }}>
                   <span>{intlFormat(t.dateFn?.().from)}</span>
                   <span style={{ margin: '0 5px' }}>-</span>
                   <span>{intlFormat(t.dateFn?.().to)}</span>
                 </div>
              }
              {
                t.label === 'Today' && <div style={{ opacity: 0.5 }}>Today</div>
              }
              {
                t.label === 'All' && <div />
              }
            </>
          )}
        </div>
      </div>
      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_MED, margin: '10px 0' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <InputLabel>Withdrawal status</InputLabel>
        <div style={{ width: 'fit-content' }}>
          <Dropdown
            selectedContent={
              <div style={{ fontSize: '14px' }}>
                {
                  withdrawalStatuses.length === Object.values(WithdrawalStatus).length
                    ? 'All withdrawals'
                    : withdrawalStatuses.length === 0
                      ? 'Choose withdrawal status'
                      : withdrawalStatuses.map(k => k.charAt(0).toUpperCase() + k.slice(1).toLowerCase()).join(', ')
                }
              </div>}
            disabled={isLoading}
            dropdownContent={
              <div>
                {Object.values(WithdrawalStatus).map((k) => (
                  <DropdownItem
                    key={k}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                    onClick={() => {
                      if (withdrawalStatuses.some(item => item === k)) {
                        setWithdrawalStatuses(prev => prev.filter(item => item !== k))
                      } else {
                        setWithdrawalStatuses(prev => [...prev, k])
                      }
                    }}
                  >
                    <Checkbox size={16} checked={withdrawalStatuses.some(item => item === k)} />
                    <span>{k.charAt(0).toUpperCase() + k.slice(1).toLowerCase()}</span>
                  </DropdownItem>
                ))}
              </div>
            }
          />
        </div>
        <InputLabel>Columns</InputLabel>
        <div style={{ width: 'fit-content' }}>
          <Dropdown
            disabled={isLoading}
            outerStyle={{ minWidth: 150 }}
            onCloseFn={_closeFn => setCloseFn(() => _closeFn)}
            selectedContent={<span>{columnTypes}</span>}
            dropdownContent={
              <div style={{ fontSize: 14 }}>
                {
                  Object.values(EXPORT_COLUMN_TYPES).filter(c => c !== columnTypes).map((c, i) =>
                    <DropdownItem key={i} onClick={() => {
                      if (c === EXPORT_COLUMN_TYPES.ALL_COLUMNS) {
                        setColumns(cloneDeep(COLUMNS))
                      }
                      setColumnTypes(c)
                      closeFn()
                    }}>{c}</DropdownItem>
                  )
                }
              </div>
            } />
        </div>
        {
          columnTypes === 'Custom' &&
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 10, columnGap: 10, padding: 5 }}>
                {
                  Object.entries(columns).map(([columnKey, columnValue]) =>
                    <div key={columnKey} style={{ fontSize: 14, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                      <Checkbox size={18} checked={!columnValue.unchecked} disabled={isLoading}
                        onClick={() => {
                          columnValue.unchecked = !columnValue.unchecked
                          setColumns(cloneDeep(columns))
                        }} />
                      <span>{columnValue.label}</span>
                    </div>
                  )
                }
              </div>
        }
        {
          columnTypes === 'All columns' &&
              <span>
                {Object.entries(columns).map(([_, l]) => l.label).join(', ')}
              </span>
        }
      </div>
    </div>

    <div style={{ padding: 30 }}>
      <PrimaryButton
        style={{ width: '100%' }}
        onClick={exportWithdrawals}
        disabled={withdrawalStatuses.length < 1 || columnsAsStrings.length < 1}
        isLoading={isLoading}>
      Export withdrawals
      </PrimaryButton>
    </div>
  </>
}

export default ExportWithdrawalsForm
